import type { BoardFillState } from '@/store/projectStore';

/** Scene is still awaiting the Unity board-fill pass (has shots with no disk thumbnail yet). */
export function isScenePendingFill(fill: BoardFillState | null, sceneId: number): boolean {
  return fill !== null && !fill.complete && fill.pendingSceneIds.includes(sceneId);
}

/**
 * Shot should render the shimmer skeleton: its scene is pending fill and it has no thumbnail
 * of its own (static-image shots carry a DB thumbnail and are never awaiting generation).
 */
export function isShotAwaitingFill(fill: BoardFillState | null, sceneId: number, hasThumbnail: boolean): boolean {
  return !hasThumbnail && isScenePendingFill(fill, sceneId);
}

/** Progress pill is shown while an announced fill is incomplete. */
export function pillVisible(fill: BoardFillState | null): boolean {
  return fill !== null && !fill.complete;
}

/** Fill phase; missing field (or no fill) reads as the full pass (pre-phase snapshots).
 *  20260909 - `cast` joins them: the pre-phase hold while Unity waits for the web's cast pipeline. Any
 *  OTHER unknown value still reads as `full`, so a future Unity phase degrades to the shot-weighted label
 *  rather than to a blank one.
 *  20261002 - `animatics` joins them: the last phase of a fill re-armed by a project-wide look change,
 *  re-rendering the animatic videos that change made stale (counted by animaticsDone/animaticsTotal). */
export function fillPhase(fill: BoardFillState | null): 'posters' | 'full' | 'cast' | 'animatics' {
  if (fill?.phase === 'posters') return 'posters';
  if (fill?.phase === 'cast') return 'cast';
  if (fill?.phase === 'animatics') return 'animatics';
  return 'full';
}

/** done/total for the phase's own unit of work, or null for a phase that counts nothing (cast). The
 *  animatics counts are optional on the wire; a missing one reads as 0 (an empty bar, never a borrowed
 *  one), though Unity always sends both with the phase. */
function phaseCounts(fill: BoardFillState): { done: number; total: number } | null {
  switch (fillPhase(fill)) {
    case 'cast': return null;
    case 'posters': return { done: fill.scenesDone, total: fill.scenesTotal };
    case 'animatics': return { done: fill.animaticsDone ?? 0, total: fill.animaticsTotal ?? 0 };
    default: return { done: fill.shotsDone, total: fill.shotsTotal };
  }
}

/** Progress fraction in [0,1]: poster phase uses scene counts; full phase uses shot counts.
 *  20260909 - the cast phase counts nothing (no render is in flight — Unity is waiting on the web), so
 *  it is a flat 0 rather than a bar borrowing another phase's numbers and appearing to stall mid-fill.
 *  20261002 - the animatics phase uses its own animatic counts. */
export function pillFraction(fill: BoardFillState | null): number {
  if (fill === null) return 0;
  const counts = phaseCounts(fill);
  if (counts === null || counts.total <= 0) return 0;
  return Math.min(1, Math.max(0, counts.done / counts.total));
}

/**
 * The progress line for a fill snapshot: its `projects`-namespace key and interpolation values.
 * ONE mapping for every surface that shows it — BoardFillPill, BoardFillModal and TeamShareOverlay
 * (which borrows the pill's line verbatim) — so a new phase cannot reach one of them and not the
 * others. 20261002: the animatics phase gets its own line; every other phase maps as before.
 */
export function fillProgressLine(fill: BoardFillState): { key: string; values?: { done: number; total: number } } {
  const phase = fillPhase(fill);
  const counts = phaseCounts(fill);
  if (phase === 'cast' || counts === null) return { key: 'sceneBoardFillCasting' };
  if (phase === 'posters') return { key: 'sceneBoardFillProgress', values: counts };
  if (phase === 'animatics') return { key: 'sceneBoardFillProgressAnimatics', values: counts };
  return { key: 'sceneBoardFillProgressShots', values: counts };
}

/** Fills at or above this many pending shots block the app behind BoardFillModal instead of the
 *  non-blocking pill (owner, 2026-09-01). shotsTotal is Unity's pre-scan count and rides the FIRST
 *  snapshot, so the choice is known before the first render. One tunable; see fillPresentation. */
export const BOARD_FILL_MODAL_SHOT_THRESHOLD = 40;

/** No snapshot change for this long while the modal is up → offer "Continue in background".
 *  CORRECTED 20260901: this used to claim a healthy fill never trips it. Unity credits shotsDone and
 *  sends its snapshot per SCENE — when a whole scene lands in the drain — never per shot, so the
 *  bar is frozen for the whole of each scene and the real window is "one scene's phase-2 render
 *  time". A single heavy scene therefore offers the escape on a perfectly healthy fill; tapping it
 *  only demotes to the pill, so the cost is a stray button, not a cancelled render. The proper fix
 *  (a per-shot snapshot from Unity) is parked for the owner FOR THE BOARD-OPEN FILL. The scoped
 *  settings-Done fill (20260902, TimelineWebViewPanel.ScopedStaleRefreshProcess) already credits
 *  and snapshots per SHOT, so its bar moves and this window measures one render, not one scene. */
export const BOARD_FILL_MODAL_STALL_MS = 90_000;

export type BoardFillPresentation = 'none' | 'pill' | 'modal';

/**
 * How the current fill presents itself. Derived from the CURRENT snapshot only, so the answer is
 * stable from the first one: a modal that appears partway through — after the user has started
 * working a live board — is worse than one that is there from the start. It therefore covers the
 * WHOLE fill, poster phase included. A resume re-scan after a canvas round-trip shrinks shotsTotal
 * to what is still pending, so a modal fill can legitimately demote to a pill; it never promotes.
 */
export function fillPresentation(fill: BoardFillState | null, dismissed: boolean): BoardFillPresentation {
  if (fill === null || fill.complete) return 'none';
  if (!dismissed && fill.shotsTotal >= BOARD_FILL_MODAL_SHOT_THRESHOLD) return 'modal';
  return 'pill';
}
